// src/utils/farmPulsePredictor.js

/**
 * FarmPulse Prediction Engine
 * ------------------------------------------------------------------
 * Provides price predictions at 1M, 3M, and 6M horizons.
 *
 *   1M  — Moving Average + Seasonal Adjustment
 *   3M  — ARIMA-like (fallback: linear regression)
 *   6M  — Hybrid (ARIMA + external signals: rainfall, transport)
 *
 * All functions are pure, stateless, and operate on arrays of
 * { t: Date, price: number } objects sorted ascending by time.
 */

import {
  SEASON_PRIOR,
  SIGNAL_WEIGHTS,
  SIGNAL_THRESHOLDS,
  PREDICTION_HORIZONS,
  MIN_POINTS_FOR_PREDICTION,
  MIN_POINTS_FOR_ARIMA,
  CONFIDENCE_LEVELS,
} from "../constants/farmPulseConstants";

// ─── Math helpers ──────────────────────────────────────────────────
const round = (v, d = 2) =>
  typeof v === "number" && isFinite(v) ? +v.toFixed(d) : v;
const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
const DAY_MS = 86_400_000;

function startOfDayMs(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

function median(arr) {
  if (!arr.length) return 0;
  const sorted = [...arr].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 0) {
    return (sorted[mid - 1] + sorted[mid]) / 2;
  }
  return sorted[mid];
}

function mean(arr) {
  if (!arr.length) return 0;
  return arr.reduce((s, v) => s + v, 0) / arr.length;
}

function stdDev(arr) {
  if (arr.length < 2) return 0;
  const m = mean(arr);
  const variance = arr.reduce((s, v) => s + (v - m) ** 2, 0) / (arr.length - 1);
  return Math.sqrt(variance);
}

// ─── Linear regression ────────────────────────────────────────────
export function linearRegression(points) {
  const n = points.length;
  if (n < 2) return null;
  const xs = points.map((p) => p.t.getTime());
  const ys = points.map((p) => p.price);
  const xMean = mean(xs);
  const yMean = mean(ys);
  let num = 0;
  let den = 0;
  for (let i = 0; i < n; i++) {
    num += (xs[i] - xMean) * (ys[i] - yMean);
    den += (xs[i] - xMean) ** 2;
  }
  if (den === 0) return null;
  const slope = num / den;
  const intercept = yMean - slope * xMean;
  const residuals = ys.map((y, i) => y - (intercept + slope * xs[i]));
  const se = stdDev(residuals);
  return { slope, intercept, se };
}

export function toDailySeries(points, maxLookbackDays = 365) {
  if (!Array.isArray(points) || points.length === 0) return [];

  const cutoff = Date.now() - maxLookbackDays * DAY_MS;
  const buckets = new Map();

  for (const p of points) {
    const t = p?.t instanceof Date ? p.t : new Date(p?.t);
    const price = Number(p?.price);
    if (!Number.isFinite(price) || Number.isNaN(t.getTime())) continue;
    if (t.getTime() < cutoff) continue;

    const dayKey = startOfDayMs(t);
    const existing = buckets.get(dayKey) || [];
    existing.push(price);
    buckets.set(dayKey, existing);
  }

  return Array.from(buckets.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([dayMs, prices]) => ({
      t: new Date(dayMs),
      price: median(prices),
    }));
}

// ─── Moving average ──────────────────────────────────────────────
function movingAverage(prices, window) {
  if (prices.length < window) return mean(prices);
  return mean(prices.slice(-window));
}

// ─── Seasonal adjustment ────────────────────────────────────────
function getSeasonKey(date) {
  const m = date.getMonth(); // 0-based: Jan=0
  if (m >= 2 && m <= 4) return "ITUMBA";      // Mar–May  (Long Rainy)
  if (m >= 5 && m <= 8) return "IMPESHYI";     // Jun–Sep  (Long Dry)
  if (m >= 9 && m <= 10) return "UMUHINDO";    // Oct–Nov  (Short Rainy)
  return "URUGARYI";                           // Dec–Feb  (Short Dry)
}

function seasonalAdjustment(crop, targetDate) {
  const seasonKey = getSeasonKey(targetDate);
  return SEASON_PRIOR[crop]?.[seasonKey] ?? 0;
}

// ─── ARIMA-like differencing model ──────────────────────────────
function arimaForecast(prices, horizon) {
  // Simple differenced AR(1) model
  if (prices.length < MIN_POINTS_FOR_ARIMA) return null;

  // First-order differencing
  const diffs = [];
  for (let i = 1; i < prices.length; i++) {
    diffs.push(prices[i] - prices[i - 1]);
  }

  // AR(1) coefficient (autocorrelation of diffs)
  const mDiff = mean(diffs);
  let num = 0;
  let den = 0;
  for (let i = 1; i < diffs.length; i++) {
    num += (diffs[i] - mDiff) * (diffs[i - 1] - mDiff);
    den += (diffs[i - 1] - mDiff) ** 2;
  }
  const phi = den !== 0 ? clamp(num / den, -0.95, 0.95) : 0;

  // Forecast forward step by step
  let lastPrice = prices[prices.length - 1];
  let lastDiff = diffs[diffs.length - 1];
  const forecasted = [];
  for (let step = 0; step < horizon; step++) {
    const nextDiff = mDiff + phi * (lastDiff - mDiff);
    lastPrice += nextDiff;
    lastDiff = nextDiff;
    forecasted.push(lastPrice);
  }

  // Residual standard error for confidence
  const residuals = [];
  let prevDiff = diffs[0];
  for (let i = 1; i < diffs.length; i++) {
    const predicted = mDiff + phi * (prevDiff - mDiff);
    residuals.push(diffs[i] - predicted);
    prevDiff = diffs[i];
  }
  const se = stdDev(residuals);

  return { value: round(forecasted[forecasted.length - 1]), se: round(se) };
}

// ─── 1M prediction: Moving Average + Seasonal ──────────────────
export function predict1M(points, crop) {
  if (points.length < MIN_POINTS_FOR_PREDICTION) return null;

  const prices = points.map((p) => p.price);
  const lastDate = points[points.length - 1].t;
  const targetDate = new Date(lastDate.getTime() + PREDICTION_HORIZONS["1M"] * DAY_MS);

  // 14-day moving average as base
  const ma = movingAverage(prices, 14);
  const seasonPct = seasonalAdjustment(crop, targetDate);
  const predicted = ma * (1 + seasonPct / 100);

  // Confidence band: stdDev of last 14 prices
  const recentPrices = prices.slice(-14);
  const se = stdDev(recentPrices);
  const confidence = computeConfidence(points.length, se, ma);

  const bounded = Math.max(1, predicted);
  return {
    horizon: "1M",
    predicted: round(bounded),
    low: round(Math.max(1, bounded - 1.96 * se)),
    high: round(Math.max(1, bounded + 1.96 * se)),
    confidence,
    method: "ma_seasonal",
    current: round(prices[prices.length - 1]),
  };
}

// ─── 3M prediction: ARIMA (fallback: linear regression) ────────
export function predict3M(points, crop) {
  if (points.length < MIN_POINTS_FOR_PREDICTION) return null;

  const prices = points.map((p) => p.price);
  const lastDate = points[points.length - 1].t;
  const targetDate = new Date(lastDate.getTime() + PREDICTION_HORIZONS["3M"] * DAY_MS);
  const seasonPct = seasonalAdjustment(crop, targetDate);

  // Try ARIMA first
  const arima = arimaForecast(prices, PREDICTION_HORIZONS["3M"]);
  if (arima) {
    const adjusted = Math.max(1, arima.value * (1 + seasonPct / 100));
    const confidence = computeConfidence(points.length, arima.se, mean(prices));
    return {
      horizon: "3M",
      predicted: round(adjusted),
      low: round(Math.max(1, adjusted - 1.96 * arima.se * Math.sqrt(3))),
      high: round(Math.max(1, adjusted + 1.96 * arima.se * Math.sqrt(3))),
      confidence,
      method: "arima",
      current: round(prices[prices.length - 1]),
    };
  }

  // Fallback: linear regression
  const reg = linearRegression(points);
  if (!reg) return null;

  const tTarget = targetDate.getTime();
  const predicted = Math.max(1, (reg.intercept + reg.slope * tTarget) * (1 + seasonPct / 100));
  const confidence = computeConfidence(points.length, reg.se, mean(prices));

  return {
    horizon: "3M",
    predicted: round(predicted),
    low: round(Math.max(1, predicted - 1.96 * reg.se * Math.sqrt(3))),
    high: round(Math.max(1, predicted + 1.96 * reg.se * Math.sqrt(3))),
    confidence,
    method: "linear_regression",
    current: round(prices[prices.length - 1]),
  };
}

// ─── 6M prediction: Hybrid (ARIMA + external signals) ──────────
export function predict6M(points, crop, externalSignals = {}) {
  if (points.length < MIN_POINTS_FOR_PREDICTION) return null;

  const prices = points.map((p) => p.price);
  const lastDate = points[points.length - 1].t;
  const targetDate = new Date(lastDate.getTime() + PREDICTION_HORIZONS["6M"] * DAY_MS);
  const seasonPct = seasonalAdjustment(crop, targetDate);

  // External signal adjustments (rainfall deviation %, transport cost change %)
  const rainfallAdj = (externalSignals.rainfallDeviation ?? 0) * 0.15;
  const transportAdj = (externalSignals.transportCostChange ?? 0) * 0.1;
  const totalExternalAdj = rainfallAdj + transportAdj;

  // Try ARIMA
  const arima = arimaForecast(prices, PREDICTION_HORIZONS["6M"]);
  let baseValue;
  let baseSE;

  if (arima) {
    baseValue = arima.value;
    baseSE = arima.se;
  } else {
    // Fallback: linear regression
    const reg = linearRegression(points);
    if (!reg) return null;
    baseValue = reg.intercept + reg.slope * targetDate.getTime();
    baseSE = reg.se;
  }

  const predicted = Math.max(1, baseValue * (1 + seasonPct / 100 + totalExternalAdj / 100));
  const confidence = computeConfidence(points.length, baseSE, mean(prices)) * 0.85; // 6M gets slight discount

  return {
    horizon: "6M",
    predicted: round(predicted),
    low: round(Math.max(1, predicted - 1.96 * baseSE * Math.sqrt(6))),
    high: round(Math.max(1, predicted + 1.96 * baseSE * Math.sqrt(6))),
    confidence: round(clamp(confidence, 0, 1), 2),
    method: arima ? "hybrid_arima" : "hybrid_regression",
    current: round(prices[prices.length - 1]),
  };
}

// ─── Confidence calculation ─────────────────────────────────────
function computeConfidence(dataPoints, se, meanPrice) {
  // More data = higher confidence; lower relative error = higher confidence
  const dataCoverage = clamp(dataPoints / 120, 0, 1); // ~4 months of daily points = full coverage
  const relativeError = meanPrice > 0 ? se / meanPrice : 1;
  const errorScore = clamp(1 - relativeError * 2, 0, 1);
  return round(clamp(dataCoverage * 0.6 + errorScore * 0.4, 0, 1), 2);
}

export function getConfidenceLevel(confidence) {
  if (confidence >= CONFIDENCE_LEVELS.HIGH.min) return "high";
  if (confidence >= CONFIDENCE_LEVELS.MEDIUM.min) return "medium";
  return "low";
}

// ─── Signal classification (for market cards) ───────────────────
export function classifySignal({ trend30, trend90, volumeTrend30, seasonAdj, proj30Pct }) {
  const score =
    (trend30 ?? 0) * SIGNAL_WEIGHTS.trend30 +
    (trend90 ?? 0) * SIGNAL_WEIGHTS.trend90 +
    (volumeTrend30 ?? 0) * SIGNAL_WEIGHTS.volumeTrend30 +
    (seasonAdj ?? 0) * SIGNAL_WEIGHTS.seasonAdj +
    (proj30Pct ?? 0) * SIGNAL_WEIGHTS.proj30Pct;

  const s = clamp(score, -50, 50);
  let label = "HOLD";
  if (s >= SIGNAL_THRESHOLDS.STRONG_BUY) label = "STRONG_BUY";
  else if (s >= SIGNAL_THRESHOLDS.BUY) label = "BUY";
  else if (s <= SIGNAL_THRESHOLDS.STRONG_AVOID) label = "STRONG_AVOID";
  else if (s <= SIGNAL_THRESHOLDS.AVOID) label = "AVOID";

  return { label, score: round(s, 1) };
}

// ─── Price status (Rising / Falling / Stable) ──────────────────
export function getPriceStatus(change7d, change7dPrev) {
  // Compare current 7d average vs previous 7d average
  if (change7d == null || change7dPrev == null) return "stable";
  const diff = change7d - change7dPrev;
  if (diff > 3) return "rising";
  if (diff < -3) return "falling";
  return "stable";
}

// ─── Full prediction bundle for a crop ──────────────────────────
export function generatePredictions(points, crop, externalSignals = {}) {
  if (!points || points.length < MIN_POINTS_FOR_PREDICTION) return null;

  const sorted = [...points].sort((a, b) => a.t - b.t);
  const daily = toDailySeries(sorted);
  if (daily.length < MIN_POINTS_FOR_PREDICTION) return null;

  return {
    "1M": predict1M(daily, crop),
    "3M": predict3M(daily, crop),
    "6M": predict6M(daily, crop, externalSignals),
  };
}

// ─── Sparkline data builder ─────────────────────────────────────
export function buildSparkline(points) {
  const sorted = [...points].sort((a, b) => a.t - b.t);
  return sorted.map((p) => ({ t: p.t, x: p.t.getTime(), y: p.price }));
}

// ─── Percent change ─────────────────────────────────────────────
export function pctChange(a, b) {
  if (a == null || b == null || a === 0) return null;
  return ((b - a) / Math.abs(a)) * 100;
}

// ─── Price validation check ─────────────────────────────────────
export function isPriceValidated(submissions, marginPct = 2) {
  if (!submissions || submissions.length < 5) return false;

  // Sort by price
  const prices = submissions.map((s) => s.price).sort((a, b) => a - b);
  const median = prices[Math.floor(prices.length / 2)];
  const margin = median * (marginPct / 100);

  // Count how many are within margin of median
  const agreed = prices.filter((p) => Math.abs(p - median) <= margin).length;
  return agreed >= 5;
}

export function getValidatedPrice(submissions, marginPct = 2) {
  if (!isPriceValidated(submissions, marginPct)) return null;
  const prices = submissions.map((s) => s.price).sort((a, b) => a - b);
  const median = prices[Math.floor(prices.length / 2)];
  return round(median);
}
